"use client";

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useMemo } from "react";
import { usePathname } from "next/navigation";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useFollowUpEnabled } from "@/hooks/use-follow-up-enabled";
import { useModule } from "@/hooks/use-module";
import { PERMISSIONS, hasAnyPermission, hasPermission } from "@/lib/permissions";
import { useSetupChecklistAvailable } from "@/api/provider/setup-checklist/setup-checklist-query";
import { useHasTrainingPlan } from "@/api/provider/training/training-query";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";

/** Reads existing providers; this hook introduces no new Context or state owner. */
export function useSidebarEnvironment() {
  const $ = _c(27);
  const pathname = usePathname();
  const {
    tenant,
    isLoading
  } = useTenant();
  const {
    currentUser
  } = useUser();
  const followUp = useFollowUpEnabled();
  const hasWaitlist = useModule("WAITLIST");
  const hasAskDecoda = useModule("ASK_DECODA");
  const setupAvailable = useSetupChecklistAvailable();
  const trainingAvailable = useHasTrainingPlan();
  let t0;
  if ($[0] !== currentUser || $[1] !== isLoading || $[2] !== pathname || $[3] !== tenant) {
    t0 = tenant?.subDomain && currentUser?.id && !isLoading && pathname.split("/")[1] === tenant.subDomain ? `${tenant.subDomain}:${currentUser.id}` : null;
    $[0] = currentUser;
    $[1] = isLoading;
    $[2] = pathname;
    $[3] = tenant;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const ownerScope = t0;
  let t1;
  if ($[5] !== currentUser) {
    t1 = hasAnyPermission(currentUser, [PERMISSIONS.AI_USE, PERMISSIONS.AI_AGENT_USE, PERMISSIONS.AI_ANALYTICS_USE, PERMISSIONS.AI_SCRIBE_USE]);
    $[5] = currentUser;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const canUseAssistant = t1;
  let t2;
  if ($[7] !== currentUser || $[8] !== hasWaitlist) {
    t2 = hasWaitlist && hasPermission(currentUser, PERMISSIONS.SCHEDULING_WAITLIST_MANAGE);
    $[7] = currentUser;
    $[8] = hasWaitlist;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const canUseWaitlist = t2;
  const t3 = canUseWaitlist && !followUp;
  const t4 = canUseAssistant && hasAskDecoda;
  let t5;
  if ($[10] !== canUseAssistant || $[11] !== followUp || $[12] !== setupAvailable || $[13] !== t3 || $[14] !== t4 || $[15] !== trainingAvailable) {
    t5 = {
      FOLLOW_UP: followUp,
      WAITLIST: t3,
      SETUP: setupAvailable,
      TRAINING: trainingAvailable,
      ASSISTANT: canUseAssistant,
      ASK_DECODA: t4
    };
    $[10] = canUseAssistant;
    $[11] = followUp;
    $[12] = setupAvailable;
    $[13] = t3;
    $[14] = t4;
    $[15] = trainingAvailable;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== ownerScope || $[18] !== pathname || $[19] !== t5) {
    t6 = {
      ownerScope,
      path: pathname,
      capabilities: t5
    };
    $[17] = ownerScope;
    $[18] = pathname;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const environment = t6;
  const storedOwner = useRightSidebarStore(_temp);
  let t7;
  let t8;
  if ($[21] !== environment) {
    t7 = () => {
      useRightSidebarStore.getState().updateEnvironment(environment);
    };
    t8 = [environment];
    $[21] = environment;
    $[22] = t7;
    $[23] = t8;
  } else {
    t7 = $[22];
    t8 = $[23];
  }
  useLayoutEffect(t7, t8);
  let t10;
  let t9;
  if ($[24] !== ownerScope) {
    t9 = () => () => {
      const state_0 = useRightSidebarStore.getState();
      if (state_0.environment.ownerScope === ownerScope) {
        state_0.updateEnvironment({
          ...state_0.environment,
          ownerScope: null
        });
      }
    };
    t10 = [ownerScope];
    $[24] = ownerScope;
    $[25] = t10;
    $[26] = t9;
  } else {
    t10 = $[25];
    t9 = $[26];
  }
  useLayoutEffect(t9, t10);
  return ownerScope !== null && storedOwner === ownerScope;
}
function _temp(state) {
  return state.environment.ownerScope;
}